import { SimpleCarousel } from '@eventbrite/collection-carousel';
import { Icon } from '@eventbrite/eds-icon';
import { ChevronDownChunky, MapPinChunky } from '@eventbrite/eds-iconography';
import { FormattedEvent } from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import { Typography } from '@eventbrite/marmalade';
import React, { useEffect, useState } from 'react';
import { LocationAutocompleteContainer } from '../../../../containers/LocationAutocompleteContainer';
import RankedCardContainer from '../../../../containers/RankedCardContainer/RankedCardContainer';
import { TOP_BANNER_FALLBACK_IMAGES } from '../../constants';
import { transformBuckets } from '../../containers/MainContent/utils';
import classes from './CityBrowseTopBanner.module.scss';
import { CityBrowseTopBannerProps } from './interface';
import { getInitialEvents, useTopBannerEventExpansions } from './utils';

const ChevronIcon = (
    <Icon color="white" size="small" type={<ChevronDownChunky />} />
);
const LocationPin = <Icon type={<MapPinChunky />} color="white" size="small" />;

export const CityBrowseTopBanner = (props: CityBrowseTopBannerProps) => {
    const {
        locationData,
        isBotRequest,
        handleLocationSelect,
        handleUseCurrentLocation,
        filteredBuckets,
        locale,
        history,
        isMobile,
    } = props;

    const h1Prefix = gettext('Best events in');

    const transformedBuckets = transformBuckets({
        buckets: filteredBuckets,
        expanded: true,
    });

    const [initialEvents, setInitialEvents] = useState<FormattedEvent[]>(
        getInitialEvents(transformedBuckets),
    );

    const { data: expandedEvents } = useTopBannerEventExpansions({
        content: initialEvents,
        bucketKey: 'top_banner_events',
        type: 'event',
    });

    useEffect(() => {
        if (expandedEvents) {
            setInitialEvents(expandedEvents);
        }
    }, [expandedEvents]);

    const numberOfEventsToPush = Math.min(initialEvents.length, 8);

    const locationPicker = (
        <span
            className={classes.cityBrowseHeaderLocationAutocomplete}
            data-current-text={locationData.currentPlace}
            data-testid="city-browse-header"
            data-spec="location-autocomplete"
            style={
                {
                    '--character-count':
                        locationData.currentPlace?.length || 10,
                } as any
            }
        >
            {isBotRequest ? (
                locationData.currentPlace
            ) : (
                <LocationAutocompleteContainer
                    handleUseCurrentLocation={handleUseCurrentLocation}
                    handleLocationSelect={handleLocationSelect}
                    suffix={ChevronIcon}
                    prefix={LocationPin}
                    history={history}
                    autocompleteProps={{
                        borderType: 'content',
                        preventInputValuePrefill: true,
                        id: 'browse-location-selector',
                    }}
                />
            )}
        </span>
    );

    return (
        <>
            <div className={classes.cityBrowseHeader}>
                <div className={classes.cityBrowseHeaderContainer}>
                    <div className={classes.cityBrowseHeaderContainerContent}>
                        {locationPicker}
                        <h1>
                            <Typography
                                variant="heading-md"
                                className="headingPrefix"
                            >
                                {h1Prefix}
                            </Typography>
                            <Typography
                                variant="heading-lg"
                                className="heading"
                            >
                                {locationData.currentPlace}
                            </Typography>
                        </h1>
                    </div>
                    {initialEvents.length === 0 ? (
                        <div
                            className={
                                classes.cityBrowseHeaderContainerFallback
                            }
                        >
                            <img
                                className={
                                    classes.cityBrowseHeaderContainerFallbackImageContainer
                                }
                                src={
                                    isMobile
                                        ? TOP_BANNER_FALLBACK_IMAGES[
                                              'imgMobile'
                                          ]
                                        : TOP_BANNER_FALLBACK_IMAGES[
                                              'imgDesktop'
                                          ]
                                }
                                alt="City Browse Top Banner Fallback Image"
                            />
                        </div>
                    ) : (
                        <div
                            className={
                                classes.cityBrowseHeaderContainerEventCarousel
                            }
                        >
                            <SimpleCarousel
                                useSideNavigation={!isMobile}
                                slidesVisible={{ desktop: 3, tablet: 2 }}
                            >
                                {initialEvents
                                    .slice(0, numberOfEventsToPush)
                                    .map((event, i) => (
                                        <RankedCardContainer
                                            affCode="ebdssbtopbannercarousel"
                                            event={event}
                                            key={i}
                                            locale={locale}
                                            rank={i + 1}
                                            useAlternativeColor={true}
                                        />
                                    ))}
                            </SimpleCarousel>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};
